import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, Check, ChevronLeft, ClipboardList, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import { isNative } from '@/lib/native-auth';
import type { FazendaResumo } from '../schemas';
import { useExcluirFazenda, useExcluirProdutor, useFazendasDoProdutor, useProdutores } from '../api';
import { CalendarioModal } from './CalendarioRelatorio';
import { PropriedadesOfflineCard } from './PropriedadesOfflineCard';
import { SeverityChip } from './SeverityChip';

function fmtHa(ha: number | null): string {
  if (ha == null) return '—';
  return `${ha.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ha`;
}

// Trator do Figma (tela 02) — glifo exato exportado; o quadrado de fundo é o
// span (bg-surface3) e o traço segue currentColor (light/dark de graça).
function TratorIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="8 10 30 27" fill="none" aria-hidden="true">
      <rect x="8.5" y="19.5" width="9" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="19" y="12.5" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="9.5" y="12.5" width="1.8" height="6" rx="0.9" fill="currentColor" />
      <circle cx="28" cy="28" r="7.5" stroke="currentColor" strokeWidth="2" />
      <circle cx="28" cy="28" r="1.75" fill="currentColor" />
      <circle cx="13" cy="30" r="4.5" stroke="currentColor" strokeWidth="2" />
      <circle cx="13" cy="30" r="1.25" fill="currentColor" />
    </svg>
  );
}

// Tela 02 · Fazendas de um produtor (mobile).
// Tela 17 · Fazendas (desktop/web): ≥lg mesma lista em 2 colunas, botão
// "Nova fazenda" no topo (substitui o FAB) e sem o card de offline (campo).
export function FazendasView({ cpf }: { cpf: string }) {
  const navigate = useNavigate();
  const { data: produtores } = useProdutores();
  const { data: fazendas, isLoading, isError } = useFazendasDoProdutor(cpf);
  const excluirProdutor = useExcluirProdutor();
  const excluirFazenda = useExcluirFazenda();

  const produtor = (produtores ?? []).find((p) => p.cpf.replace(/\D/g, '') === cpf.replace(/\D/g, ''));
  const nome = produtor?.nome ?? 'Produtor';
  const n = (fazendas ?? []).length;

  // "Adicionar": nova fazenda, visita técnica cobrindo várias fazendas, ou o
  // consolidado dessas fazendas. As duas últimas passam pela escolha das áreas.
  const [menuAberto, setMenuAberto] = useState(false);
  const [escolhendo, setEscolhendo] = useState<'visita' | 'relatorio' | null>(null);
  const [calendario, setCalendario] = useState<number[] | null>(null);
  // Dois wrappers do mesmo menu (topo no desktop, FAB no mobile) — cada um com
  // seu ref, senão o clique DENTRO do menu visível cai como "clique fora" pelo
  // ref do wrapper escondido e o menu fecha sozinho.
  const menuTopoRef = useRef<HTMLDivElement | null>(null);
  const menuFabRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuAberto) return undefined;
    function fora(e: MouseEvent) {
      const alvo = e.target as Node;
      const dentro =
        menuTopoRef.current?.contains(alvo) === true || menuFabRef.current?.contains(alvo) === true;
      if (!dentro) setMenuAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [menuAberto]);

  function escolher(acao: 'fazenda' | 'visita' | 'relatorio') {
    setMenuAberto(false);
    if (acao === 'fazenda') { void navigate({ to: '/agronomia/nova-fazenda', search: { cpf } }); return; }
    setEscolhendo(acao);
  }

  function confirmarSelecao(ids: number[]) {
    const acao = escolhendo;
    setEscolhendo(null);
    if (!ids.length) return;
    if (acao === 'visita') void navigate({ to: '/agronomia/visita-multi', search: { imoveis: ids.join(',') } });
    else setCalendario(ids);
  }

  async function excluirArea(f: FazendaResumo) {
    if (excluirFazenda.isPending) return;
    const nt = f.talhoes;
    const detalhe = nt ? ` e seus ${nt} ${nt === 1 ? 'talhão' : 'talhões'} (com visitas, fotos e amostras)` : '';
    if (!window.confirm(`Excluir a área "${f.fazenda ?? 'esta área'}"${detalhe}?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await excluirFazenda.mutateAsync(f.imovel_id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Falha ao excluir a área.');
    }
  }

  async function excluir() {
    if (excluirProdutor.isPending) return;
    const nf = produtor?.fazendas ?? n;
    const detalhe = nf ? ` e todas as suas ${nf} ${nf === 1 ? 'fazenda' : 'fazendas'} (talhões, visitas, fotos e amostras)` : '';
    if (!window.confirm(`Excluir o produtor "${nome}"${detalhe}?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await excluirProdutor.mutateAsync(cpf);
      void navigate({ to: '/agronomia' });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Falha ao excluir o produtor.');
    }
  }

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 lg:px-8 lg:pt-7 lg:pb-4">
        <div className="flex items-center gap-1 lg:gap-1.5">
          <button
            type="button"
            onClick={() => void navigate({ to: '/agronomia' })}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2 lg:-ml-2 lg:size-8 lg:text-text-2 lg:hover:text-text"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} className="lg:hidden" />
            <ChevronLeft size={22} strokeWidth={2} className="hidden lg:block" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text lg:text-xl lg:font-semibold lg:leading-7">
            {nome}
          </h1>
          {/* excluir produtor (com todas as áreas) */}
          <button
            type="button"
            onClick={() => void excluir()}
            disabled={excluirProdutor.isPending}
            aria-label="Excluir produtor"
            title="Excluir produtor"
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-[#fbeceb] hover:text-[#b3261e] disabled:opacity-50 lg:size-8 dark:hover:bg-[#3a1f1d]"
          >
            {excluirProdutor.isPending ? (
              <Loader2 size={18} strokeWidth={2} className="animate-spin" />
            ) : (
              <Trash2 size={18} strokeWidth={1.75} />
            )}
          </button>
          {/* desktop: Adicionar no topo (no mobile é FAB) */}
          <div ref={menuTopoRef} className="relative hidden lg:block">
            <button
              type="button"
              onClick={() => setMenuAberto((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-[18px] py-[11px] text-sm font-medium text-[var(--on-solid)]"
            >
              <Plus size={16} strokeWidth={2.25} /> Adicionar
            </button>
            {menuAberto && <MenuAdicionar onEscolher={escolher} alinhar="direita" />}
          </div>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2 lg:pl-0 lg:text-sm">
          {produtor?.fazendas ?? n} fazendas · {fmtHa(produtor?.total_ha ?? null)}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40 lg:px-8 lg:pb-8">
        {/* download offline é fluxo de campo — só no app (APK) */}
        {isNative() && <PropriedadesOfflineCard cpf={cpf} />}

        <p className="px-1 pb-2 pt-1 text-[0.72rem] font-semibold uppercase tracking-wider text-accent lg:px-0 lg:pt-0 lg:text-[11px] lg:font-medium lg:tracking-[0.5px]">
          Fazendas
        </p>

        {isLoading && <p className="py-8 text-center text-sm text-text-muted">Carregando…</p>}
        {isError && <p className="py-8 text-center text-sm text-text-2">Não foi possível carregar as fazendas.</p>}
        {!isLoading && !isError && n === 0 && (
          <p className="py-10 text-center text-sm text-text-muted">Nenhuma fazenda cadastrada.</p>
        )}

        <div className="flex flex-col gap-2.5 lg:grid lg:grid-cols-2 lg:gap-x-3.5 lg:gap-y-3">
          {(fazendas ?? []).map((f) => (
            <FazendaRow
              key={f.imovel_id}
              f={f}
              onClick={() => void navigate({ to: '/agronomia/fazendas/$id', params: { id: String(f.imovel_id) } })}
              onExcluir={() => void excluirArea(f)}
              excluindo={excluirFazenda.isPending && excluirFazenda.variables === f.imovel_id}
            />
          ))}
        </div>
      </div>

      <div ref={menuFabRef} className="absolute bottom-24 right-4 z-10 lg:hidden">
        {menuAberto && <MenuAdicionar onEscolher={escolher} alinhar="acima" />}
        <button
          type="button"
          onClick={() => setMenuAberto((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] shadow-[var(--shadow-over)]"
        >
          <Plus size={20} strokeWidth={2.25} /> Adicionar
        </button>
      </div>

      {escolhendo && (
        <SelecionarFazendas
          fazendas={fazendas ?? []}
          titulo={escolhendo === 'visita' ? 'Visita técnica' : 'Relatório consolidado'}
          descricao={
            escolhendo === 'visita'
              ? 'Escolha as fazendas que entram nesta visita. O talhão de cada foto é identificado pelo GPS.'
              : 'Escolha as fazendas que entram no relatório consolidado.'
          }
          onCancelar={() => setEscolhendo(null)}
          onConfirmar={confirmarSelecao}
        />
      )}

      {calendario && (
        <CalendarioModal
          imovelIds={calendario}
          fazenda={nome}
          onClose={() => setCalendario(null)}
        />
      )}
    </div>
  );
}

// Escolha de fazendas (visita ou relatório que cobrem mais de uma).
function SelecionarFazendas({
  fazendas,
  titulo,
  descricao,
  onCancelar,
  onConfirmar,
}: {
  fazendas: FazendaResumo[];
  titulo: string;
  descricao: string;
  onCancelar: () => void;
  onConfirmar: (ids: number[]) => void;
}) {
  const [sel, setSel] = useState<number[]>([]);
  const alterna = (id: number) =>
    setSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const todas = sel.length === fazendas.length && fazendas.length > 0;

  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-black/45 lg:items-center" onClick={onCancelar}>
      <div
        className="flex max-h-[86vh] w-full flex-col overflow-hidden rounded-t-[24px] bg-bg lg:max-w-[460px] lg:rounded-[18px] lg:border lg:border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 px-5 pb-3 pt-4">
          <div className="mx-auto mb-3 h-1 w-9 rounded-full bg-surface3 lg:hidden" />
          <h2 className="text-[1.1rem] font-semibold text-text">{titulo}</h2>
          <p className="mt-1 text-[0.82rem] leading-snug text-text-2">{descricao}</p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">
          <button
            type="button"
            onClick={() => setSel(todas ? [] : fazendas.map((f) => f.imovel_id))}
            className="mb-2 text-[0.8rem] font-semibold text-accent"
          >
            {todas ? 'Limpar seleção' : 'Selecionar todas'}
          </button>
          <div className="flex flex-col gap-2">
            {fazendas.map((f) => {
              const marcada = sel.includes(f.imovel_id);
              return (
                <button
                  key={f.imovel_id}
                  type="button"
                  onClick={() => alterna(f.imovel_id)}
                  className={`flex w-full items-center gap-3 rounded-[12px] border px-3 py-2.5 text-left ${
                    marcada ? 'border-[var(--accent)] bg-[var(--accent-100)]' : 'border-[var(--agro-card-border)] bg-surface2'
                  }`}
                >
                  <span
                    className={`flex size-5 shrink-0 items-center justify-center rounded-[6px] border-2 ${
                      marcada ? 'border-[var(--accent)] bg-[var(--accent)] text-white' : 'border-[#bdb9b4]'
                    }`}
                  >
                    {marcada && <Check size={13} strokeWidth={3} />}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[0.9rem] font-medium text-text">{f.fazenda ?? 'Fazenda'}</span>
                    <span className="text-[0.78rem] text-text-2">
                      {[f.municipio, fmtHa(f.area_ha)].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 border-t border-border px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
          <button
            type="button"
            disabled={sel.length === 0}
            onClick={() => onConfirmar(sel)}
            className="flex w-full items-center justify-center rounded-full bg-[var(--accent)] py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
          >
            Continuar{sel.length > 0 ? ` · ${sel.length} ${sel.length === 1 ? 'fazenda' : 'fazendas'}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// Menu do "Adicionar": cadastrar mais uma fazenda, abrir uma visita técnica que
// cobre várias fazendas do produtor, ou gerar o consolidado dessas fazendas.
function MenuAdicionar({
  onEscolher,
  alinhar,
}: {
  onEscolher: (acao: 'fazenda' | 'visita' | 'relatorio') => void;
  alinhar: 'direita' | 'acima';
}) {
  const itens = [
    { key: 'fazenda' as const, icon: MapPin, label: 'Nova fazenda' },
    { key: 'visita' as const, icon: ClipboardList, label: 'Nova visita técnica' },
    { key: 'relatorio' as const, icon: CalendarDays, label: 'Relatório consolidado' },
  ];
  const pos = alinhar === 'direita' ? 'right-0 top-[52px]' : 'bottom-[60px] right-0';
  return (
    <div
      className={`absolute ${pos} z-20 min-w-[232px] overflow-hidden rounded-[14px] border border-[var(--agro-card-border)] bg-surface shadow-[var(--shadow-over)]`}
    >
      {itens.map((it, i) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onEscolher(it.key)}
          className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[0.9rem] text-text hover:bg-surface2 ${i > 0 ? 'border-t border-border' : ''}`}
        >
          <it.icon size={18} strokeWidth={1.75} className="shrink-0 text-accent" />
          {it.label}
        </button>
      ))}
    </div>
  );
}

function FazendaRow({
  f,
  onClick,
  onExcluir,
  excluindo,
}: {
  f: FazendaResumo;
  onClick: () => void;
  onExcluir: () => void;
  excluindo: boolean;
}) {
  const local = [f.municipio, f.uf].filter(Boolean).join('/');
  return (
    <div className="flex w-full items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 py-3 pl-3 pr-2 lg:gap-3.5 lg:rounded-[14px] lg:py-[13px] lg:pl-3.5 lg:pr-2.5">
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99] lg:gap-3.5"
      >
        <span className="flex size-11 shrink-0 items-center justify-center rounded-[10px] bg-surface3 text-[#3a3530] dark:text-[var(--text2)]">
          <TratorIcon size={28} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 lg:gap-[2px]">
          <span className="truncate text-[0.9rem] font-medium text-text lg:text-base lg:font-semibold lg:leading-[22px]">
            {f.fazenda ?? 'Fazenda'}
          </span>
          <span className="text-[0.8rem] text-text-2 lg:text-[13px] lg:leading-[18px]">
            {[local, `${fmtHa(f.area_ha)} · ${f.talhoes} ${f.talhoes === 1 ? 'talhão' : 'talhões'}`]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </button>
      <SeverityChip severity="ok" />
      <button
        type="button"
        onClick={onExcluir}
        disabled={excluindo}
        aria-label={`Excluir área ${f.fazenda ?? ''}`.trim()}
        title="Excluir área"
        className="flex size-9 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-[#fbeceb] hover:text-[#b3261e] disabled:opacity-50 dark:hover:bg-[#3a1f1d]"
      >
        {excluindo ? (
          <Loader2 size={16} strokeWidth={2} className="animate-spin" />
        ) : (
          <Trash2 size={16} strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
}
